/* =====================================================================================
   Bank: what the account says happened, and tying it to what we invoiced.

   READ ONLY, AND THE SCHEMA ENFORCES IT. Forum approves and records; it does not pay.
   Nothing in this file can move money, there is no payment-initiation call anywhere in
   the product, and the provider connection holds a read scope.

   WHO SEES IT IS A LIST, NOT A ROLE. ledger.bank_accounts is protected by an explicit
   per-account allowlist (ledger.bank_account_viewers), so an admin of the tenant who has
   not been named sees nothing at all. That is why every query here is written plainly
   with no service-role escape: if the caller is not on the list, the correct result IS
   an empty screen, and the UI says so rather than pretending the feed is broken.

   WHY IT MATTERS FOR REVENUE. A credit matched to a payment allocates to an invoice, and
   that invoice's revenue schedule then recognises against cash the bank confirms rather
   than against somebody's recollection of a statement. Unmatched credits are the work
   queue; internal transfers are excluded from it, because counting money moved between
   your own accounts as income is the most common way a cash figure becomes a lie.
   ===================================================================================== */
import { BACKEND } from './db';
import { explain } from './dberrors';

const live = () => BACKEND === 'supabase';

export interface BankAccount {
  id: string; name: string; provider: string; currency: string;
  identifier_last4: string | null; scope: string;
  last_synced_at: string | null; active: boolean;
}

export interface BankTxn {
  id: string; bank_account_id: string; booked_on: string;
  amount: number; currency: string; direction: 'credit' | 'debit';
  description: string | null; counterparty: string | null;
  reference: string | null; payment_id: string | null;
  is_internal: boolean; reconciled_at: string | null;
  /* What kind of money this is, once somebody has decided. Null means undecided,
     which for a credit means it is still assumed to be revenue awaiting an invoice. */
  category: 'revenue' | 'own_transfer' | 'capital_in' | 'capital_out'
          | 'funding' | 'refund' | 'cost' | 'other' | null;
}

export interface SyncRun {
  id: string; started_at: string; finished_at: string | null;
  status: 'running' | 'ok' | 'failed';
  fetched: number; inserted: number; message: string | null;
}

export async function fetchAccounts(): Promise<BankAccount[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('ledger').from('bank_accounts')
    .select('*').order('name');
  if (error) throw new Error(explain(error.message, 'read the bank feed'));
  return data ?? [];
}

export async function fetchTransactions(accountId?: string): Promise<BankTxn[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  let q = supabase().schema('ledger').from('bank_transactions')
    .select('*').order('booked_on', { ascending: false }).limit(500);
  if (accountId) q = q.eq('bank_account_id', accountId);
  const { data, error } = await q;
  if (error) throw new Error(explain(error.message, 'read the bank feed'));
  return data ?? [];
}

export async function fetchSyncRuns(): Promise<SyncRun[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('ledger').from('bank_sync_runs')
    .select('*').order('started_at', { ascending: false }).limit(5);
  if (error) throw new Error(explain(error.message, 'read the sync history'));
  return data ?? [];
}

/* Mark a line as a transfer between the company's own accounts. It leaves the
   unreconciled queue and never counts as income or as cost. */
export async function setInternal(id: string, internal: boolean) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase()
    .schema('ledger').from('bank_transactions')
    .update({ is_internal: internal }).eq('id', id);
  if (error) throw new Error(explain(error.message, 'update this transaction'));
}

/* Tie a credit to a payment already on the ledger. The database stamps who did it
   and when; this call does not get to assert either. */
export async function matchToPayment(txnId: string, paymentId: string | null) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase()
    .schema('ledger').from('bank_transactions')
    .update({ payment_id: paymentId }).eq('id', txnId);
  if (error) throw new Error(explain(error.message, 'match this transaction'));
}

/* Candidate payments for a credit: same currency, unallocated, near the amount.
   Deliberately narrow. A wrong match is worse than no match, because it recognises
   revenue against the wrong invoice and nobody looks at it again. */
export async function candidatePayments(txn: BankTxn) {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('ledger').from('payments')
    .select('id, received_on, amount, currency, bank_reference, unallocated')
    .eq('currency', txn.currency)
    .gte('amount', Math.abs(txn.amount) * 0.98)
    .lte('amount', Math.abs(txn.amount) * 1.02)
    .order('received_on', { ascending: false }).limit(10);
  if (error) throw new Error(explain(error.message, 'find matching payments'));
  return data ?? [];
}

/* ------------------------------------------------------------------ capital

   Money in that is not revenue and is not a transfer between our own accounts: a
   director's loan, share capital, or a loan from somebody else. Recording it here is
   what keeps the company's obligation to repay visible; marking it an own transfer,
   which is the tempting single click, makes the debt vanish. */

export interface CapitalBalance {
  id: string; name: string; kind: string; currency: string;
  balance: number; introduced: number; repaid: number;
  last_movement: string | null; active: boolean;
}

export async function fetchCapitalBalances(): Promise<CapitalBalance[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('ledger').from('capital_balances')
    .select('*').order('name');
  if (error) throw new Error(explain(error.message, 'read capital accounts'));
  return data ?? [];
}

export async function createCapitalAccount(a: {
  name: string; kind: string; currency: string;
}) {
  const { supabase, currentMember } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('ledger').from('capital_accounts')
    /* currentMember() returns the member ROW; created_by wants its uuid. Sending the whole
       object froze the funder dialog on production: Postgres refused the insert, the button's
       await rejected, and nothing caught it. */
    .insert({ name: a.name, kind: a.kind, currency: a.currency,
              created_by: (await currentMember())?.id ?? null })
    .select('id').single();
  if (error) throw new Error(explain(error.message, 'open a capital account'));
  return data.id as string;
}

/* Classify a bank credit as money from a funder, and write the movement that records
   what the company now owes them. The two happen together on purpose: a category with
   no movement behind it is a label, not a liability. */
export async function recordCapitalIn(txn: BankTxn, capitalAccountId: string) {
  const { supabase, currentMember } = await import('./supabase');
  const me = await currentMember();
  const { error: e1 } = await supabase()
    .schema('ledger').from('capital_movements')
    .insert({
      capital_account_id: capitalAccountId,
      moved_on: txn.booked_on,
      amount: txn.amount,
      currency: txn.currency,
      direction: Number(txn.amount) >= 0 ? 'introduced' : 'repaid',
      bank_transaction_id: txn.id,
      notes: 'Bank credit: ' + (txn.description ?? ''),
      created_by: me?.id ?? null,
    });
  if (e1) throw new Error(explain(e1.message, 'record this funding'));
  const { error: e2 } = await supabase()
    .schema('ledger').from('bank_transactions')
    .update({ category: Number(txn.amount) >= 0 ? 'capital_in' : 'capital_out',
              is_internal: false })
    .eq('id', txn.id);
  if (e2) throw new Error(explain(e2.message, 'classify this transaction'));
}

export const KIND_LABEL: Record<string, string> = {
  director_loan: "Director's loan",
  share_capital: 'Share capital',
  third_party_loan: 'Third-party loan',
};

export const money = (n: number, ccy: string) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: ccy || 'GBP',
  }).format(n);
